<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Check, RotateCcw, Search, Sparkles, X } from '@lucide/vue'
import {
  Badge, Button, EmptyState, Input, Pagination, Section, Select, Spinner, Table, Textarea, Toolbar, useToast,
} from '@drama/ui'

import { listPrompts, recallPrompts, reviewPrompt } from '../../api/prompt-library.js'
import { formatDate } from '../intent-video/format.js'

const { t } = useI18n()
const toast = useToast()

const MODALITIES = ['video', 'image', 'audio']
const QUALITIES = ['draft', 'approved', 'rejected']

// ------------------------------------------------------------------ 列表

const loading = ref(false)
const error = ref('')
const items = ref([])
const total = ref(0)
const page = ref(1)
const pages = ref(1)
const pageSize = ref(50)
const filters = reactive({ modality: '', quality: '', pack_id: '' })
const reviewingId = ref('')

const modalityOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.promptLibrary.filters.allModalities') },
  ...MODALITIES.map(m => ({ value: m, label: m })),
])
const qualityOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.promptLibrary.filters.allQualities') },
  ...QUALITIES.map(q => ({ value: q, label: t(`visualGameAdmin.promptLibrary.quality.${q}`) })),
])

const columns = computed(() => [
  { key: 'quality', label: t('visualGameAdmin.promptLibrary.table.quality'), width: '90px' },
  { key: 'modality', label: t('visualGameAdmin.promptLibrary.table.modality'), width: '80px' },
  { key: 'pack_id', label: t('visualGameAdmin.promptLibrary.table.pack'), width: '160px', mono: true },
  { key: 'prompt_text', label: t('visualGameAdmin.promptLibrary.table.promptText'), width: '360px' },
  { key: 'source', label: t('visualGameAdmin.promptLibrary.table.source'), width: '140px' },
  { key: 'created_at', label: t('visualGameAdmin.promptLibrary.table.createdAt'), width: '160px' },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '160px', align: 'right', fixed: 'right' },
])

const summary = computed(() => t('visualGameAdmin.assetLibrary.pager.summary', { total: total.value, page: page.value, pages: pages.value }))

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const data = await listPrompts({
      modality: filters.modality || undefined,
      quality: filters.quality || undefined,
      pack_id: filters.pack_id.trim() || undefined,
      page: page.value,
      limit: pageSize.value,
    })
    items.value = data.items
    total.value = data.total
    pages.value = data.pages
    page.value = data.page
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function applyFilters() {
  page.value = 1
  load()
}

function resetFilters() {
  filters.modality = ''
  filters.quality = ''
  filters.pack_id = ''
  applyFilters()
}

function prevPage() { page.value -= 1; load() }
function nextPage() { page.value += 1; load() }
function changePageSize(size) { pageSize.value = size; applyFilters() }

async function review(row, quality) {
  reviewingId.value = row.id
  try {
    const { prompt } = await reviewPrompt(row.id, quality)
    const index = items.value.findIndex(item => item.id === prompt.id)
    if (index !== -1) items.value[index] = prompt
    toast.success(t('visualGameAdmin.promptLibrary.reviewed'))
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    reviewingId.value = ''
  }
}

function qualityVariant(quality) {
  if (quality === 'approved') return 'success'
  if (quality === 'rejected') return 'danger'
  return 'default'
}

// ------------------------------------------------------------------ 召回测试（R1）

const recallText = ref('')
const recallModality = ref('')
const recallPackId = ref('')
const recalling = ref(false)
const recallResult = ref(null)

async function runRecall() {
  if (recallText.value.trim() === '') return
  recalling.value = true
  try {
    recallResult.value = await recallPrompts({
      text: recallText.value.trim(),
      modality: recallModality.value || null,
      pack_id: recallPackId.value.trim() || null,
      limit: 8,
    })
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    recalling.value = false
  }
}

const recallColumns = computed(() => [
  { key: 'similarity', label: t('visualGameAdmin.promptLibrary.table.similarity'), width: '90px', mono: true },
  { key: 'modality', label: t('visualGameAdmin.promptLibrary.table.modality'), width: '80px' },
  { key: 'prompt_text', label: t('visualGameAdmin.promptLibrary.table.promptText'), width: '420px' },
])
</script>

<template>
  <div class="plp vga-page" data-insp-path="apps/visual-game-admin/src/pages/prompt-library/PromptLibraryPage.vue">
    <Section>
      <Toolbar>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.promptLibrary.table.modality') }}</label>
          <Select v-model="filters.modality" :options="modalityOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.promptLibrary.table.quality') }}</label>
          <Select v-model="filters.quality" :options="qualityOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.promptLibrary.table.pack') }}</label>
          <Input v-model="filters.pack_id" size="sm" :placeholder="t('visualGameAdmin.promptLibrary.filters.packPlaceholder')" @keyup.enter="applyFilters" />
        </div>
        <template #actions>
          <Button size="sm" :loading="loading" @click="applyFilters">
            <Search :size="14" /> {{ t('visualGameAdmin.assetLibrary.filters.apply') }}
          </Button>
          <Button variant="ghost" size="sm" :disabled="loading" @click="resetFilters">
            <RotateCcw :size="14" /> {{ t('visualGameAdmin.common.reset') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <template v-else>
      <Section :padded="false">
        <EmptyState v-if="items.length === 0" :title="t('visualGameAdmin.promptLibrary.emptyTitle')" />
        <Table v-else :columns="columns" :rows="items">
          <template #quality="{ row }">
            <Badge :variant="qualityVariant(row.quality)" size="sm">{{ t(`visualGameAdmin.promptLibrary.quality.${row.quality}`) }}</Badge>
          </template>
          <template #pack_id="{ row }">{{ row.pack_id ?? '—' }}</template>
          <template #prompt_text="{ row }">
            <span class="plp__clamp" :title="row.prompt_text">{{ row.prompt_text }}</span>
          </template>
          <template #source="{ row }">{{ row.source }}<span v-if="row.source_ref"> · {{ row.source_ref }}</span></template>
          <template #created_at="{ row }">{{ formatDate(row.created_at) }}</template>
          <template #actions="{ row }">
            <div class="plp__actions">
              <Button
                variant="ghost" size="sm" :disabled="row.quality === 'approved'" :loading="reviewingId === row.id"
                @click="review(row, 'approved')"
              >
                <Check :size="14" /> {{ t('visualGameAdmin.promptLibrary.approve') }}
              </Button>
              <Button
                variant="ghost" size="sm" :disabled="row.quality === 'rejected'" :loading="reviewingId === row.id"
                @click="review(row, 'rejected')"
              >
                <X :size="14" /> {{ t('visualGameAdmin.promptLibrary.reject') }}
              </Button>
            </div>
          </template>
        </Table>
      </Section>
      <Pagination
        :has-prev="page > 1" :has-next="page < pages" :loading="loading" :summary="summary"
        :prev-label="t('visualGameAdmin.assetLibrary.pager.prev')" :next-label="t('visualGameAdmin.assetLibrary.pager.next')"
        :page-size="pageSize" :page-size-options="[20, 50, 100]" :page-size-label="t('visualGameAdmin.assetLibrary.pager.pageSize')"
        @prev="prevPage" @next="nextPage" @update:page-size="changePageSize"
      />
    </template>

    <Section :title="t('visualGameAdmin.promptLibrary.recall.title')">
      <p class="plp__hint">{{ t('visualGameAdmin.promptLibrary.recall.hint') }}</p>
      <div class="plp__recall-box">
        <Textarea v-model="recallText" :rows="2" class="plp__recall-input" :placeholder="t('visualGameAdmin.promptLibrary.recall.placeholder')" />
        <div class="plp__recall-controls">
          <Select v-model="recallModality" :options="modalityOptions" size="sm" />
          <Input v-model="recallPackId" size="sm" :placeholder="t('visualGameAdmin.promptLibrary.filters.packPlaceholder')" />
          <Button size="sm" :loading="recalling" @click="runRecall">
            <Sparkles :size="14" /> {{ t('visualGameAdmin.promptLibrary.recall.run') }}
          </Button>
        </div>
      </div>
      <div v-if="recallResult" class="plp__recall-result">
        <EmptyState v-if="recallResult.results.length === 0" :title="t('visualGameAdmin.promptLibrary.recall.noHitTitle')" />
        <Table v-else :columns="recallColumns" :rows="recallResult.results">
          <template #similarity="{ row }">{{ row.similarity.toFixed(3) }}</template>
          <template #prompt_text="{ row }">
            <span class="plp__clamp" :title="row.prompt_text">{{ row.prompt_text }}</span>
          </template>
        </Table>
      </div>
    </Section>
  </div>
</template>

<style scoped>
.plp__actions { display: flex; gap: var(--drama-space-xs); justify-content: flex-end; }
.plp__clamp { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; line-height: 1.5; }
.plp__hint { margin: 0 0 var(--drama-space-sm); font-size: 12px; color: var(--drama-color-text-muted); }
.plp__recall-box { display: flex; gap: var(--drama-space-sm); align-items: flex-start; }
.plp__recall-input { flex: 1; }
.plp__recall-controls { display: flex; gap: var(--drama-space-sm); flex-direction: column; min-width: 160px; }
.plp__recall-result { margin-top: var(--drama-space-md); }
</style>
